import React, {
  useEffect, useMemo, useRef,
} from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IMaterialInfo, ITIYMaterialData } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { Container, OwnershipRequirementType } from '../../../../symphony';

import { ProgrammingLanguageToCodeLanguages } from '../../../../shared/public/constants/constants';
import { SlEventsService } from '../../services/Business/sl-events.service';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlDataService } from '../../services/sl-data.service';
import TIYWidgetOutput from '../../components/TIYWidgetOutput/TIYWidgetOutput';

import s from './TIYWidgetContainer.scss';
import { SlLoader } from '../../../../shared/public/SlLoader/SlLoader';
import { AISolutions } from '../../components/Header/RightSide/AISolutions';
import { lePlaygroundContext } from '../../global-constants';
import { WidgetPlayground } from '../../../../shared/public/SlCodeEditorComponents/WidgetPlayground/WidgetPlayground';
import { CodeLanguages } from '../../../../api/public/global';

interface ITIYWidgetContainer {
  materialInfo: IMaterialInfo;
}

export function TIYWidgetContainer({ materialInfo }: ITIYWidgetContainer) {
  useStyles(s);

  const eventService = Container.take(lePlaygroundContext.value, SlEventsService);

  const initialCodes = (materialInfo.data as ITIYMaterialData)?.codes;

  const codeConfigs = materialInfo.header.uiConfigurations?.codeEditors;

  const shouldShowProLabel = materialInfo.status.ownership.ownershipRequirementsTypeId === OwnershipRequirementType.Pro;

  const {
    isTIYResetButtonActive,
    output$: output,
    isWeb$: isWeb,
    loading$: loading,
    minimizedTIYEditorRefs,
    sourceCode$: sourceCode,
    cssCode$: cssCode,
    jsCode$: jsCode,
    isTIYExpanded,
  } = useContainerData(lePlaygroundContext.value, SlDataService, [
    'isTIYResetButtonActive',
    'output$',
    'isWeb$',
    'loading$',
    'minimizedTIYEditorRefs',
    'sourceCode$',
    'cssCode$',
    'jsCode$',
    'isTIYExpanded'
  ]);

  const editorRef1 = useRef(null);
  const editorRef2 = useRef(null);
  const editorRef3 = useRef(null);

  useEffect(() => {
    eventService.onTIYWidgetMount(materialInfo, [editorRef1, editorRef2, editorRef3]);
  }, [materialInfo]);

  const handleRunClick = () => {
    eventService.onTIYWidgetRunClick();
  };

  const handleExpandClick = () => {
    eventService.onTIYExpandClick();
  };

  const handleClickToEdit = (idx: number) => {
    eventService.onTIYEditClick(idx);
  };

  const handleChangeContent = ({ value, lng }) => {
    eventService.onTIYWidgetInputChange(value, lng);
  };

  const handleFocus = () => {
    eventService.onTIYWidgetFocus(materialInfo.status);
  };

  const handleResetClick = () => {
    eventService.onTIYEditorResetClick();
  };

  const codes = useMemo(() => {
    if (isWeb) {
      return initialCodes.map((code) => {
        const language = ProgrammingLanguageToCodeLanguages[code.languageId];
        if (language === CodeLanguages.JS) {
          return jsCode || code.code;
        } if (language === CodeLanguages.CSS) {
          return cssCode || code.code;
        }
        return sourceCode || code.code;
      });
    }
    return [sourceCode || initialCodes[0].code];
  }, [sourceCode, cssCode, jsCode, isWeb]);

  return (
    <>
      {loading && <SlLoader backgroundColor="rgba(0, 0, 0, 0.5)" fullScreen />}
      {initialCodes && <div className="tiy-widget-container">
        <WidgetPlayground
          initialCodes={initialCodes}
          minimizedRefs={minimizedTIYEditorRefs}
          onExpandClick={handleExpandClick}
          onFocus={handleFocus}
          shouldShowProLabel={shouldShowProLabel}
          aiSolutionComponent={materialInfo.status.ownership?.isOwned && ProgrammingLanguageToCodeLanguages[initialCodes[0].languageId] !== CodeLanguages.SQL && <AISolutions isTIYWidget />}
          onEditClick={handleClickToEdit}
          onChange={handleChangeContent}
          codes={codes}
          onResetClick={handleResetClick}
          isResetButtonActive={isTIYResetButtonActive}
          onRunClick={handleRunClick}
          defaultCodeLanguage={codeConfigs?.find((code) => code.isDefault)?.languageId || initialCodes[0].languageId}
        />
        {!isTIYExpanded && <TIYWidgetOutput output={output} isWeb={isWeb} />}
      </div>}
    </>
  );
}
